import styled, { css } from 'styled-components'
import { StyledButtonProps } from './types'
import { getButtonFontSize, getButtonPadding, getButtonRadius } from './utils'

export const StyledButtonText = styled.span``

export const StyledButtonIcon = styled.span``

const defaultButtonStyles = css<StyledButtonProps>`
  border-radius: var(--border-radius);
  display: inline-flex;
  font-size: ${({ $size = 'default' }) => getButtonFontSize($size)};
  font-weight: 600;
  justify-content: center;
`

const primaryButtonStyles = css<StyledButtonProps>`
  color: ${p => (p?.$inverted ? 'var(--c-base-theme-3)' : p.theme.colors.white[1])};

  :not(:disabled) {
    &:before {
      background-color: ${p => (p?.$inverted ? p.theme.colors.white[1] : 'var(--c-theme-2)')};
    }

    &.active,
    &:hover,
    &:active {
      &:before {
        background-color: ${p => (p?.$inverted ? p.theme.colors.white[1] : 'var(--c-theme-1)')};
        bottom: -4px;
        left: -4px;
        right: -4px;
        top: -4px;
      }
    }

    &.active,
    &:active {
      color: ${p => (p?.$inverted ? 'var(--c-theme-2)' : p.theme.colors.black[1])};

      &:before {
        background-color: ${p => (p?.$inverted ? p.theme.colors.white[2] : 'var(--c-theme-6)')};
      }
    }
  }

  &:disabled {
    cursor: not-allowed;
    color: ${p => (p?.$inverted ? p.theme.colors.white[4] : 'var(--c-theme-4)')};

    &:before {
      background-color: ${p => (p?.$inverted ? p.theme.colors.white[6] : 'var(--c-theme-7)')};
    }
  }
`

const secondaryButtonStyle = css<StyledButtonProps>`
  border-radius: var(--border-radius);
  border-color: ${p => (p?.$inverted ? p.theme.colors.white[3] : 'var(--c-theme-6)')};
  color: ${p => (p?.$inverted ? p.theme.colors.white[1] : 'var(--c-theme-1)')};

  :not(:disabled) {
    &:before {
      background-color: transparent;
      opacity: 0;
      transform: scale(0);
    }

    &:hover,
    &.active,
    &:active,
    &:focus,
    &:focus-within {
      &:before {
        bottom: -4px;
        left: -4px;
        opacity: 1;
        right: -4px;
        top: -4px;
        transform: scale(1);
      }
    }

    &:hover {
      color: ${p => (p?.$inverted ? p.theme.colors.bluetiful[3] : p.theme.colors.white[1])};

      &:before {
        background-color: ${p => (p?.$inverted ? p.theme.colors.white[1] : 'var(--c-theme-1)')};
      }
    }

    &.active,
    &:active,
    &:focus,
    &:focus-within {
      color: var(--c-theme-1);

      &:before {
        background-color: ${p => (p?.$inverted ? p.theme.colors.white[2] : 'var(--c-theme-6)')};
      }
    }
  }

  &:disabled {
    color: ${p => (p?.$inverted ? p.theme.colors.white[4] : 'var(--c-theme-4)')};
    border-color: ${p => (p?.$inverted ? p.theme.colors.white[6] : 'var(--c-theme-6)')};
  }
`

const tertiaryButtonStyles = css<StyledButtonProps>`
  color: ${p => (p.$inverted ? 'var(--c-theme-inverted)' : 'var(--c-theme-1)')};

  :not(:disabled) {
    &:before {
      background-color: transparent;
      opacity: 0;
      transform: scale(0);
    }

    &:hover,
    &.active,
    &:active,
    &:focus,
    &:focus-within {
      &:before {
        opacity: 1;
        transform: scale(1);
      }
    }

    &:hover {
      color: ${p => (p.$inverted ? 'var(--c-theme-2)' : p.theme.colors.white[1])};

      &:before {
        background-color: ${p => (p.$inverted ? p.theme.colors.white[1] : 'var(--c-theme-1)')};
      }
    }

    &.active,
    &:active,
    &:focus,
    &:focus-within {
      color: var(--c-theme-1);

      &:before {
        background-color: var(--c-theme-6);
      }
    }
  }

  &:disabled {
    color: ${p => (p.$inverted ? p.theme.colors.white[4] : 'var(--c-theme-4)')};
  }
`

const redButtonStyles = css<StyledButtonProps>`
  ${defaultButtonStyles}
  color: ${p => (p?.$inverted ? p.theme.colors.red[3] : p.theme.colors.white[1])};
  background-color: ${p => p?.$inverted ? p.theme.colors.white[1] : p.theme.colors.red[3]};
  border-color: ${p => p.theme.colors.red[5]};

  &:hover {
    background-color: ${p => (p?.$inverted ? p.theme.colors.white[1] : p.theme.colors.red[3])}; 
    color: ${p => (p?.$inverted ? p.theme.colors.red[3] : p.theme.colors.white[1])};
    transform: scale(1.03);
  }
`

const greenButtonStyles = css<StyledButtonProps>`
  ${defaultButtonStyles}
  color: ${p => p.theme.colors.white[1]};
  background-color: ${p => p?.$inverted ? p.theme.colors.white[1] : p.theme.colors.green[3]};

  &:hover {
    background-color: ${p => (p?.$inverted ? p.theme.colors.white[1] : p.theme.colors.green[3])}; 
    color: ${p => (p?.$inverted ? p.theme.colors.white[1] : p.theme.colors.white[1])};
    transform: scale(1.03);
  }
`
const blueButtonStyles = css<StyledButtonProps>`
  ${defaultButtonStyles}
  color: ${p => p.theme.colors.white[1]};
  background-color: ${p => p?.$inverted ? p.theme.colors.white[1] : p.theme.colors.bluetiful[3]};

  &:hover {
    background-color: ${p => (p?.$inverted ? p.theme.colors.white[1] : p.theme.colors.bluetiful[3])}; 
    color: ${p => (p?.$inverted ? p.theme.colors.white[1] : p.theme.colors.white[1])};
    transform: scale(1.03);
  }
`

const yellowButtonStyles = css<StyledButtonProps>`
  ${defaultButtonStyles}
  color: ${p => p.theme.colors.white[1]};
  background-color: ${p => p?.$inverted ? p.theme.colors.white[1] : p.theme.colors.yellow[3]};

  &:hover {
    background-color: ${p => (p.theme.colors.yellow[3])}; 
    color: ${p => (p.theme.colors.white[1])};
    transform: scale(1.03);
  }
`

const defaultSizeStyles = css<StyledButtonProps>`
  padding: ${({ $isIconButton }) => ($isIconButton ? '0.7rem' : '0.7rem 0.8rem')};
`

const linkButtonStyles = css<StyledButtonProps>`
  padding: 0 !important;
  background: transparent;
  color: ${p => p.theme.colors.bluetiful[3]};
  ${p => p.$inverted && css`
    color: ${p.theme.colors.black[1]};
  `}
`

const smallSizeStyles = css<StyledButtonProps>`
  padding: ${({ $isIconButton }) => ($isIconButton ? '0.4rem' : '0.4rem 0.6rem')};
`

const xsmallSizeStyles = css<StyledButtonProps>`
  padding: ${({ $isIconButton }) => ($isIconButton ? '0.25rem' : '0.25rem 0.4rem')};
`

export const StyledButton = styled.button<StyledButtonProps>`
  --border-radius: ${({ $size }) => getButtonRadius($size)};
  align-items: center;
  appearance: none;
  background: transparent;
  border: 2px solid transparent;
  display: inline-flex;
  font-size: ${({ $size = 'default' }) => getButtonFontSize($size)};
  font-weight: 600;
  padding: ${({ $size = 'default' }) => getButtonPadding($size)};
  justify-content: center;
  line-height: 1.2;
  position: relative;
  text-decoration: none;
  transition-timing-function: cubic-bezier(0.79, 0.14, 0.15, 0.86);
  transition: 200ms;
  user-select: none;
  white-space: nowrap;
  gap: 0.5rem;

  ${p =>
    p.$fullWidth &&
    css`
      display: flex;
      justify-content: center;
      text-align: left;
      width: 100%;
    `}

  [class^='theme-'] & {
    --c-theme-1: var(--c-base-theme-2);
    --c-theme-2: var(--c-base-theme-3);
    --c-theme-3: var(--c-base-theme-3);
    --c-theme-4: var(--c-base-theme-4);
    --c-theme-5: var(--c-base-theme-5);
    --c-theme-6: var(--c-base-theme-5);
    --c-theme-7: var(--c-base-theme-7);
  }

  .theme-reset & {
    --c-theme-1: ${p => p.theme.colors.black[1]} !important;
    --c-theme-2: ${p => p.theme.colors.black[2]} !important;
    --c-theme-3: ${p => p.theme.colors.black[3]} !important;
    --c-theme-4: ${p => p.theme.colors.black[4]} !important;
    --c-theme-5: ${p => p.theme.colors.black[5]} !important;
    --c-theme-6: ${p => p.theme.colors.black[6]} !important;
    --c-theme-7: ${p => p.theme.colors.black[7]} !important;
  }

  ${p =>
    p.$ignoreTheme &&
    css`
      --c-theme-1: ${p.theme.colors.black[1]} !important;
      --c-theme-2: ${p.theme.colors.black[2]} !important;
      --c-theme-3: ${p.theme.colors.black[3]} !important;
      --c-theme-4: ${p.theme.colors.black[4]} !important;
      --c-theme-5: ${p.theme.colors.black[5]} !important;
      --c-theme-6: ${p.theme.colors.black[6]} !important;
      --c-theme-7: ${p.theme.colors.black[7]} !important;
    `}

  &:not(:disabled) {
    cursor: pointer;
  }

  &:active,
  &:hover,
  &:focus {
    background: transparent;
  }

  &:before {
    border-radius: var(--border-radius);
    bottom: -2px;
    content: '';
    left: -2px;
    position: absolute;
    right: -2px;
    top: -2px;
    transition: 200ms;
    transition-timing-function: cubic-bezier(0.79, 0.14, 0.15, 0.86);
    z-index: 0;
  }

  & > * {
    position: relative;
    z-index: 1;
  }

  ${p => p.$variant === 'primary' && primaryButtonStyles}
  ${p => p.$variant === 'secondary' && secondaryButtonStyle}
  ${p => p.$variant === 'tertiary' && tertiaryButtonStyles}
  ${p => p.$variant === 'red' && redButtonStyles}
  ${p => p.$variant === 'green' && greenButtonStyles}
  ${p => p.$variant === 'blue' && blueButtonStyles}
  ${p => p.$variant === 'yellow' && yellowButtonStyles}
  ${p => p.$variant === 'link' && linkButtonStyles}

  ${({ $size = 'default' }) => $size === 'default' && defaultSizeStyles}
  ${({ $size = 'default' }) => $size === 'small' && smallSizeStyles}
  ${({ $size = 'default' }) => $size === 'xsmall' && xsmallSizeStyles}
`
